/**
 * Console capture utility — intercepts console.error calls and stores them
 * in a rolling buffer for inclusion in bug reports.
 */

interface ConsoleError {
  timestamp: string; // ISO 8601
  message: string;
  stack?: string;
}

const MAX_ERRORS = 20;
// Cap each captured message so a stray object argument (e.g. a serialized
// request payload) can't smuggle large amounts of customer data into a bug
// report. Defense-in-depth alongside DEV-gating the payloads at their source. (#275)
const MAX_MESSAGE_CHARS = 2000;
const errorBuffer: ConsoleError[] = [];
let initialized = false;

/**
 * Initialize the console error capture. Safe to call multiple times —
 * subsequent calls are no-ops.
 */
export function initConsoleCapture(): void {
  if (initialized) return;
  initialized = true;

  const originalError = console.error.bind(console);

  console.error = (...args: unknown[]): void => {
    // Never swallow — call original first
    originalError(...args);

    // Build a single message string from all arguments
    const rawMessage = args
      .map((arg) => {
        if (arg instanceof Error) return arg.message;
        if (typeof arg === 'string') return arg;
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      })
      .join(' ');
    const message =
      rawMessage.length > MAX_MESSAGE_CHARS
        ? `${rawMessage.slice(0, MAX_MESSAGE_CHARS)}… [truncated ${rawMessage.length - MAX_MESSAGE_CHARS} chars]`
        : rawMessage;

    // Extract stack if the first argument is an Error
    const stack = args[0] instanceof Error ? args[0].stack : undefined;

    const entry: ConsoleError = {
      timestamp: new Date().toISOString(),
      message,
      ...(stack !== undefined && { stack }),
    };

    if (errorBuffer.length >= MAX_ERRORS) {
      errorBuffer.shift();
    }
    errorBuffer.push(entry);
  };
}

/**
 * Returns a snapshot copy of the captured error buffer (most recent last).
 */
export function getRecentErrors(): ConsoleError[] {
  return [...errorBuffer];
}
